import axios from 'axios'
import { inject } from 'vue'
import { isEmpty } from 'lodash'
import { useAuthStore } from '../stores/AuthStore'
import { handleErrors } from '../utils/errorHandler'
import { getDatabase, ref as dbRef, get as dbGet } from 'firebase/database'
import { getApp } from 'firebase/app'
import connectionService from './ConnectionService'
import serverHealthService from './ServerHealthService'
import navigationMonitor from './NavigationMonitorService'

const axiosConfig = {
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Access-Control-Allow-Origin': '*'
  }
}

const axiosInstance = axios.create(axiosConfig)

const pdfCache = new Map() 
const pdfInFlight = new Map() 

// RequestService() is called from many components/stores. Interceptors must
// be registered only once on the shared axiosInstance, otherwise each call
// stacks another identical interceptor, running auth/connection/401 logic
// once per registration for every single request.
let interceptorsRegistered = false

export const RequestService = () => {
  const toggleLoadingWithMessage = inject('toggleLoadingWithMessage', () => {})
  const functionsBaseUrl = inject('functionsBaseUrl')

  if (!interceptorsRegistered) {
    interceptorsRegistered = true

    axiosInstance.interceptors.request.use(
      config => {
        const token = localStorage.getItem('userToken')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        // Let the browser set Content-Type with boundary for FormData
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type']
        }
        // Pending-request registry: lets navigation-anomaly events (e.g. a
        // loading timeout) report which requests never finished.
        navigationMonitor.trackRequestStart(config)
        serverHealthService.watchRequest(config)
        return config
      },
      error => {
        return Promise.reject(error)
      }
    )

    axiosInstance.interceptors.response.use(
      response => {
        connectionService.reportConnectionRestored()
        serverHealthService.reportServerRecovered()
        serverHealthService.unwatchRequest(response.config)
        navigationMonitor.trackRequestEnd(response.config)
        return response
      },
      async error => {
        const originalRequest = error.config
        serverHealthService.unwatchRequest(originalRequest)
        navigationMonitor.trackRequestEnd(originalRequest, {
          failed: true,
          status: error.response?.status ?? null,
          code: error.code ?? null
        })

        // A missing response with ERR_NETWORK means the request never reached the
        // server (no connectivity). This is the only signal we trust to show the
        // blocking offline modal, avoiding false positives from navigator.onLine.
        // Skipped when the watchdog already proved the gateway itself is down, so
        // the offline modal never stacks on top of the server-problem one.
        if (error.code === 'ERR_NETWORK' && !error.response && !serverHealthService.isServerDegraded()) {
          connectionService.reportConnectionFailure()
        }

        /*
         * A session that has not passed the second factor is not a token problem.
         * Refreshing would succeed, the retry would fail the same way, and the 401 branch
         * below would end up signing out a user who is one code away from finishing. Flag it
         * and let the app route to the code step; navigation is NOT done here, because
         * importing the router would close the cycle router → AuthStore → RequestService.
         */
        const apiErrorCode = error.response?.data?.error?.code || error.response?.data?.code || ''
        if (apiErrorCode === 'mfa_required') {
          const authStore = useAuthStore()
          authStore.markMfaPending()
          return Promise.reject(error)
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true
          const authStore = useAuthStore()
          try {
            const newToken = await authStore.refreshToken()
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            // Preserve window.history.state: this entry is the browser-history
            // bridge's 'current' sentinel. Wiping it with {} corrupted the sentinel,
            // and the next browser Back entered an infinite Back↔Forward loop.
            window.history.replaceState(window.history.state, document.title, window.location.pathname)
            return axiosInstance(originalRequest)
          } catch (refreshError) {
            authStore.logout()
            return Promise.reject(refreshError)
          }
        }

        return Promise.reject(error)
      }
    )
  }

  /*
   * Swallows request errors and resolves to undefined by default. `rethrow: true` propagates
   * the axios error instead (like `post`), so the caller can read the gateway's
   * { success, error, message } body from error.response.
   */
  const simpleGet = async (key, url, { rethrow = false } = {}) => {
    try {
      let response = await axiosInstance({
        method: 'get',
        url
      })

      if (response.status !== 200) {
        handleErrors(key, response)
      } else if (response.data?.success === false) {
        handleErrors(key, response)
      } else {
        return response
      }
    } catch (error) {
      handleErrors(key, error)
      if (rethrow) throw error
    }
  }

  const put = async (key, url, data) => {
    try {
      let response = await axiosInstance({
        method: 'put',
        url,
        data
      })

      if (response.status !== 200) {
        handleErrors(key, response)
      } else if (response.data?.success === false) {
        handleErrors(key, response)
      } else {
        return response
      }
    } catch (error) {
      handleErrors(key, error)
    }
  }

  const post = async (key, url, data, config = {}) => {
    try {
      let response = await axiosInstance({
        method: 'post',
        url,
        data,
        ...config,
      })

      if (response.status !== 200) {
        handleErrors(key, response)
      } else if (response.data?.success === false) {
        handleErrors(key, response)
      } else {
        return response
      }
    } catch (error) {
      handleErrors(key, error)
      throw error
    }
  }

  const createLog = async (entry = {}) => {
    const url = import.meta.env.VITE_CREATE_LOG_URL
    return post('createLog', url, entry)
  }


  const isPdfCached = (cacheKey) => {
    return cacheKey && pdfCache.has(cacheKey)
  }

  const getCacheStats = () => {
    return {
      size: pdfCache.size,
      keys: Array.from(pdfCache.keys())
    }
  }

  const clearPdfCache = (cacheKey = null) => {
    if (cacheKey) {
      pdfCache.delete(cacheKey)
    } else {
      pdfCache.clear()
    }
  }

  const downloadPdf = async ({
    eventData,
    fileName,
    templateID,
    data,
    cacheKey,
    precacheOnly = false,
    showLoading = true,
  }) => {
    if (!templateID || !data || !fileName) {
      handleErrors('Downloading PDF', { message: 'Missing required parameters' })
      return
    }
    const pdfFileName = `${fileName}.pdf`
    try {
      if (cacheKey && pdfCache.has(cacheKey)) {
        
        if (!precacheOnly) {
          if (showLoading === false) toggleLoadingWithMessage(false, null)
          const cachedBlob = pdfCache.get(cacheKey)
          const url = window.URL.createObjectURL(cachedBlob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', pdfFileName)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
          if (!isEmpty(eventData)) createLog(eventData)
        }
        return
      }
  
      if (cacheKey && pdfInFlight.has(cacheKey)) {
        const inflight = pdfInFlight.get(cacheKey)
        const pdfBlob = await inflight
        if (!precacheOnly) {
          if (showLoading === false) toggleLoadingWithMessage(false, null)
          const url = window.URL.createObjectURL(pdfBlob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', pdfFileName)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
          if (!isEmpty(eventData)) createLog(eventData)
        }
        return
      }
  
      const generate = async () => {
        if (showLoading) {
          toggleLoadingWithMessage(true, {
            title: 'Generating document...',
            body: 'Please wait while we create your PDF.'
          })
        }
  
        const body = { templateID, data, fileName, eventData }
        const response = await post(
          'pdfDownload',
          `${functionsBaseUrl}/utilityGateway/api/v1/exports/pdf`,
          body,
          { responseType: 'arraybuffer' }
        )
  
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' })
        return pdfBlob
      }
  
      let pdfPromise
      if (cacheKey) {
        pdfPromise = generate()
        pdfInFlight.set(cacheKey, pdfPromise)
      } else {
        pdfPromise = generate()
      }
  
      const pdfBlob = await pdfPromise
  
      if (cacheKey) {
        pdfCache.set(cacheKey, pdfBlob)
        pdfInFlight.delete(cacheKey)
        
        if (pdfCache.size > 20) {
          const firstKey = pdfCache.keys().next().value
          pdfCache.delete(firstKey)
        }
      }
  
      if (precacheOnly) {
        if (showLoading === false) toggleLoadingWithMessage(false, null)
        return
      }
  
      const url = window.URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', pdfFileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
  
      if (showLoading) toggleLoadingWithMessage(false, null)
      if (!isEmpty(eventData)) createLog(eventData)
    } catch (err) {
      handleErrors('Downloading PDF', err)
      if (showLoading) toggleLoadingWithMessage(false, null)
      if (cacheKey) pdfInFlight.delete(cacheKey)
    }
  }

  const del = async (key, url, data) => {
    try {
      let response = await axiosInstance({
        method: 'delete',
        url,
        data
      })

      if (response.status !== 200) {
        handleErrors(key, response)
      } else if (response.data?.success === false) {
        handleErrors(key, response)
      } else {
        return response
      }
    } catch (error) {
      handleErrors(key, error)
    }
  }

  const getRealtimeDatabaseValue = async (path) => {
    try {
      const db = getDatabase(getApp())
      const dbReference = dbRef(db, path)
      const snapshot = await dbGet(dbReference)
      if (snapshot.exists()) {
        return snapshot.val()
      }
    } catch (error) {
      handleErrors(`Realtime Database - Loading value from path: ${path}`, error)
    }
    return null
  }

  return {
    simpleGet,
    post,
    put,
    del,
    createLog,
    handleErrors,
    downloadPdf,
    getRealtimeDatabaseValue,
    isPdfCached,
    getCacheStats,
    clearPdfCache
  }
}